import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { apiLongRunningRequestTimeoutMs } from "../config";
import { IBulkIntelligencePricingResponse, IIntelligencePricingResponse, IPartLookupResponse } from "../models/PartLookup";

export default function usePartsLookupMutations(partId: string) {
  const { get, post, put } = useApiHelper();
  // Intelligence pricing queries the live marketplace per part, so it regularly
  // runs past the default JSON timeout - especially the bulk variant.
  const { get: getLongRunning, post: postLongRunning } = useApiHelper({
    timeoutMs: apiLongRunningRequestTimeoutMs,
  });
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60 * 60 * 4; //4 hours

  const lookupMutation = useMutation(
    () => get<IPartLookupResponse>(`/partlookup/part/${partId}`),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(["partslookup", "part", partId], data);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const intelligencePricingMutation = useMutation(
    () =>
      getLongRunning<IIntelligencePricingResponse>(
        `/partlookup/intelligence-pricing/${partId}`
      )
  );

  // Variant that takes the part id as the mutation argument so it can be
  // called for arbitrary parts (e.g. bulk running intelligence on selected rows).
  const intelligencePricingForPart = useMutation(
    (id: string) =>
      getLongRunning<IIntelligencePricingResponse>(`/partlookup/intelligence-pricing/${id}`)
  );

  const intelligencePricingBulk = useMutation(
    (partIds: string[]) =>
      postLongRunning<{ partIds: string[] }, IBulkIntelligencePricingResponse>(`/partlookup/intelligence-pricing/bulk`, { partIds })
  );

  return {
    lookupMutation,
    intelligencePricingMutation,
    intelligencePricingForPart,
    intelligencePricingBulk,
  };
}
